<template>
    <div class="home">
        <b-container fluid>
            <h1 class="mb-5">{{$keycloak.fullName}}'s Dashboard</h1>

            <b-row v-if="this.limit">
                <b-alert show variant="danger" class="h-100">
                    <p class="mb-2">Our records indicate that you are attempting to supervise more than 4 temporary piercers or/and more than 2 temporary tattooists. You will need to submit a supervisory plan for each of your current and pending supervisees.</p>
                </b-alert>
            </b-row>

            <div v-if="$keycloak.authenticated
            && (!$keycloak.hasResourceRole('mdh-staff')  && !$keycloak.hasResourceRole('ie-staff')
            && !$keycloak.hasResourceRole('mdh-admin') && !$keycloak.hasResourceRole('readonly')
            && !$keycloak.hasResourceRole('finance-staff'))">
                <b-row class="align-self-stretch">
                    <b-col cols="12">
                        <b-alert show variant="danger">
                            Do <span class="font-weight-bold">not</span> use Safari or Internet Explorer when completing an application or renewal.
                            Only use Google Chrome, Microsoft Edge, or Firefox.
                        </b-alert>
                    </b-col>
                </b-row>
                <b-row class="align-self-stretch">
                    <b-col cols="6" v-if="licenses.length === 0">
                        <b-alert show variant="warning" class="h-100">
                            <p class="mb-2">Already licensed? Please click below to claim your license.</p>
                            <b-button variant="primary" size="sm" to="claim" role="button">Claim Your License</b-button>
                        </b-alert>
                    </b-col>
                    <b-col cols="6">
                        <b-alert show variant="warning" class="h-100">
                            <p class="mb-2">First time applicants, Guest artist or if you once held a license more than six years ago, please click here to start the application process.</p>
                            <b-button variant="primary" size="sm" to="application" role="button">New Application</b-button>
                        </b-alert>
                    </b-col>
                </b-row>
            </div>
            <b-row class="mt-4 mb-4" v-if="$keycloak.authenticated && !$keycloak.hasResourceRole('mdh-staff')
                && !$keycloak.hasResourceRole('mdh-admin') && !$keycloak.hasResourceRole('ie-staff')
                && !$keycloak.hasResourceRole('readonly')">
              <b-col>
                If you have an expired license or are unable to claim your license, please contact our office at <a href="mailto:health.ba@state.mn.us">health.ba@state.mn.us</a> or call 651-201-3770 for assistance.
              </b-col>
            </b-row>
            <b-row class="mt-4 mb-4" v-if="$keycloak.authenticated && $keycloak.hasResourceRole('readonly')">
                <b-col>
                    Welcome to the Minnesota Body Art Technician Licensing System.
                    Please use the 'MDH Search' link above to locate specific license details.
                </b-col>
            </b-row>

            <div v-if="$keycloak.authenticated && (!$keycloak.hasResourceRole('mdh-staff')
            && !$keycloak.hasResourceRole('ie-staff') && !$keycloak.hasResourceRole('mdh-admin')
            && !$keycloak.hasResourceRole('readonly'))">
              <b-row v-show="drafts && drafts.length > 0" class="mt-2">
                <b-col>
                  <h2 class="green-header">Your Drafts</h2>
                  <b-table hover small responsive show-empty class="sr-caption"
                    id="draftQueue"
                    :fields="draftFields"
                    :items="drafts"
                    label-sort-asc=""
                    label-sort-desc=""
                    caption="Drafts table.  To sort the data in this table by a specific column,
                        go to the appropriate column header cell and use enter to sort the data ascending.
                        Use enter again to sort descending.">
                    <template v-slot:cell(actions)="row">
                      <b-button size="sm" @click="editDraft(row.item)" class="mr-1">
                        edit
                      </b-button>
                      <b-button size="sm" @click="deleteDraft(row.item)" class="mr-1">
                        delete
                      </b-button>
                    </template>
                    <template v-slot:empty="scope">
                        <p>No draft requests found.</p>
                    </template>
                  </b-table>
                </b-col>
              </b-row>
            </div>

            <div v-if="$keycloak.authenticated && (!$keycloak.hasResourceRole('mdh-staff')
            && !$keycloak.hasResourceRole('ie-staff') && !$keycloak.hasResourceRole('mdh-admin')
            && !$keycloak.hasResourceRole('readonly'))">
              <b-row v-if="licenses.length > 0">
                  <b-col cols="12">
                      <h2 class="green-header">Your Licenses</h2>
                      <TableList slug="licenses" :items="licenses"/>
                  </b-col>
              </b-row>



              <b-row v-if="affirmations.length > 0">
                  <b-col cols="12">
                      <h2 class="green-header">Your Affirmations</h2>
                      <AffirmationTableList v-on:attachment-added="attachmentAdded" slug="affirmations" :items="affirmations"/>
                  </b-col>
              </b-row>

              <b-row v-if="oldAffirmations.length > 0">
                  <b-col cols="12">
                      <h2 class="green-header">Your Processed Affirmations</h2>
                      <SupervisorTableList v-on:attachment-added="attachmentAdded" slug="oldAffirmations" :items="oldAffirmations" @on-update="loadUserAffirmations"/>
                  </b-col>
              </b-row>
          </div>

            <b-row v-if="$keycloak.authenticated && ($keycloak.hasResourceRole('mdh-staff') || $keycloak.hasResourceRole('finance-staff'))">
              <b-col>
                  <h2 class="green-header">Pending Applications</h2>
                  <AdminTableList slug="pendingApplications" :items="pendingApplications"/>
              </b-col>
            </b-row>

            <b-row v-if="$keycloak.authenticated && $keycloak.hasResourceRole('mdh-staff')">
                <b-col>
                    <h2 class="green-header">Updated Applications</h2>
                    <AdminTableList slug="updatedApplications" :items="updatedApplications"/>
                </b-col>
            </b-row>

            <b-row v-if="$keycloak.authenticated && $keycloak.hasResourceRole('ie-staff') ">
              <b-col>
                  <h2 class="green-header">I & E Screenings</h2>
                  <ScreeningTableList slug="screenings" :items="screenings"/>
              </b-col>
            </b-row>

            <b-row v-if="$keycloak.authenticated && $keycloak.hasResourceRole('ie-staff')">
                <b-col>
                    <h2 class="green-header">Updated I & E Screenings</h2>
                    <ScreeningTableList slug="updatedScreenings" :items="updatedScreenings"/>
                </b-col>
            </b-row>

            <b-row v-if="$keycloak.authenticated && $keycloak.hasResourceRole('ie-staff')">
                <b-col>
                    <h2 class="green-header">Pending Credentialing Approval</h2>
                    <ScreeningTableList slug="screeningsPendingApproval" :items="screeningsPendingApproval"/>
                </b-col>
            </b-row>

            
        </b-container>

    </div>
</template>

<script>
import TableList from '@/components/templates/TableList'
import AdminTableList from '@/components/templates/AdminTableList'
import ScreeningTableList from '@/components/templates/ScreeningTableList'
import AffirmationTableList from '@/components/templates/AffirmationTableList'
import SupervisorTableList from '@/components/templates/SupervisorTableList'
import axios from 'axios'

export default {
  components: {
    TableList,
    AdminTableList,
    ScreeningTableList,
    AffirmationTableList,
    SupervisorTableList,
  },
  data: () => ({
    licenses: [],
    pendingApplications: [],
    screenings: [],
    updatedApplications: [],
    updatedScreenings: [],
    screeningsPendingApproval: [],
    affirmations: [],
    oldAffirmations: [],
    drafts: null,
    draftFields: [
        {key: 'createDate', sortable: true, label: 'Last Updated'},
        {key: 'payload.category', sortable: true, label: 'License Category'},
        {key: 'payload.subCategory', sortable: true, label: 'License Subcategory'},
        {key: 'actions', label: 'Actions'},
    ],
      limit: false
  }),
  
  computed: {
    userRole() {
      return this.$store.state.userRole
    },
  },

  mounted() {
    if (this.$keycloak) {
        this.loadLicenses()
        this.loadPendingAffirmations()
        this.loadPendingApplications()
        this.loadPendingScreenings()
        this.loadUpdatedApplications()
        this.loadUpdatedScreenings()
        this.loadUserAffirmations()
        this.loadScreeningsWithPendingApproval()
        this.loadLimit()
    }

    this.loadDrafts()
  },

  methods: {

      loadLicenses() {
        axios.get(`${process.env.VUE_APP_API_BASE_URL}/license/user`)
          .then(response => {
              this.licenses = response.data
              this.licenses.forEach(l => {
                  if (l.workDates === null) {
                      l.workDates = []
                  }
                  if (l.pendingWorkDates != null) {
                      l.pendingWorkDates.forEach(p => {
                          l.workDates.push(p)
                      })
                  }
              })
          })
      },

      loadPendingApplications() {
        axios.get(`${process.env.VUE_APP_API_BASE_URL}/approval/pending`)
          .then(response => { 
            this.pendingApplications = response.data 
          })
      },

      loadUpdatedApplications() {
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/approval/updated`)
              .then(response => {
                  this.updatedApplications = response.data
              })
      },

      loadPendingScreenings() {
        axios.get(`${process.env.VUE_APP_API_BASE_URL}/screenings`)
          .then(response => {
            this.screenings = response.data
          })
      },

      loadUpdatedScreenings() {
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/screenings/updated`)
              .then(response => {
                  this.updatedScreenings = response.data
              })
      },

      loadScreeningsWithPendingApproval() {
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/screenings/pending-approval`)
              .then(response => {
                  this.screeningsPendingApproval = response.data
              })
      },

      loadPendingAffirmations() {
        axios.get(`${process.env.VUE_APP_API_BASE_URL}/supervisor/user/pending`)
          .then(response => {
            this.affirmations = response.data
          })
      },

      loadUserAffirmations() {
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/supervisor/user`)
              .then(response => {
                  this.oldAffirmations = response.data
              })
      },

      loadDrafts() {
        axios.get(`${process.env.VUE_APP_API_BASE_URL}/drafts/user`)
          .then(response => {
            if (response.data) {
              this.drafts = response.data
            } else {
              this.drafts = []
            }
          })
      },
      loadLimit(){
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/supervisor/user/plan`)
              .then(response => {
                  this.limit = response.data;
              })
      },

      editDraft(item) {
          if (item.payload.reclassify == null) {
              this.$router.push({ name: 'applicationDraft', params: { draftId: item.id } })
          } else {
              this.$router.push({ name: 'reclassifyDraft', params: { draftId: item.id } })
          }
      },

      deleteDraft(item) {
        this.$bvModal.msgBoxConfirm('Are you sure?')
          .then(value => {
            if (value) {
               axios.delete(`${process.env.VUE_APP_API_BASE_URL}/drafts/${item.id}`)
                .then(response => { 
                  this.loadDrafts()
                })
            }
            })
          .catch(err => {
            // An error occurred
          })
      },
      attachmentAdded(){
          this.loadPendingAffirmations();
          this.loadUserAffirmations();
      }
  },
  created() {
    document.title = "Dashboard - Body Art"
  }
}
</script>

<style>
 .green-header {
     color: #60A609;
 }
</style>